import { useState, useEffect } from "react";
import { api } from "@/App";
import { Plus, Trash2, Edit, User, Shield, Users, Key, Eye, EyeOff, Settings2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";

// Définition des menus disponibles
const AVAILABLE_MENUS = [
  { id: "planning", label: "Planning", description: "Vue calendrier des RDV" },
  { id: "today", label: "Aujourd'hui", description: "RDV du jour" },
  { id: "kanban", label: "Kanban", description: "Vue workflow par étape" },
  { id: "dossiers", label: "Dossiers", description: "Liste de tous les véhicules" },
  { id: "clients", label: "Clients", description: "Base de données clients" },
  { id: "vehicules", label: "Véhicules", description: "Base de données véhicules" },
  { id: "new_rdv", label: "Nouveau RDV", description: "Créer un rendez-vous" },
  { id: "rentabilite", label: "Rentabilité", description: "Suivi des coûts et bénéfices" },
  { id: "parametres", label: "Paramètres", description: "Configuration de l'application" },
];

// Définition des actions disponibles
const AVAILABLE_ACTIONS = [
  { id: "create_rdv", label: "Créer des RDV", description: "Ajouter de nouveaux rendez-vous" },
  { id: "edit_rdv", label: "Modifier les RDV", description: "Éditer les informations des véhicules" },
  { id: "delete_rdv", label: "Supprimer des RDV", description: "Supprimer des rendez-vous" },
  { id: "view_client_info", label: "Voir infos clients", description: "Téléphone, email, adresse" },
  { id: "send_sms", label: "Envoyer des SMS", description: "Notifier les clients" },
  { id: "advance_workflow", label: "Avancer le workflow", description: "Passer à l'étape suivante" },
  { id: "validate_step", label: "Valider son étape", description: "Marquer sa tâche comme terminée" },
  { id: "add_comment", label: "Ajouter commentaires", description: "Commenter sur les dossiers" },
  { id: "mark_pieces", label: "Gérer les pièces", description: "Marquer pièces commandées" },
];

// Permissions par défaut selon la spécialité
const DEFAULT_PERMISSIONS = {
  admin: {
    menus: ["planning", "today", "kanban", "dossiers", "clients", "vehicules", "new_rdv", "rentabilite", "parametres"],
    actions: ["create_rdv", "edit_rdv", "delete_rdv", "view_client_info", "send_sms", "advance_workflow", "validate_step", "add_comment", "mark_pieces"]
  },
  secretaire: {
    menus: ["planning", "today", "kanban", "dossiers", "clients", "vehicules", "new_rdv", "rentabilite"],
    actions: ["create_rdv", "edit_rdv", "view_client_info", "send_sms", "advance_workflow", "add_comment", "mark_pieces"]
  },
  chef_atelier: {
    menus: ["planning", "today", "kanban", "dossiers"],
    actions: ["edit_rdv", "view_client_info", "advance_workflow", "validate_step", "add_comment", "mark_pieces"]
  },
  carrossier: {
    menus: ["planning", "today", "kanban"],
    actions: ["validate_step", "add_comment"]
  },
  peintre: {
    menus: ["planning", "today", "kanban"],
    actions: ["validate_step", "add_comment"]
  },
  mecanicien: {
    menus: ["planning", "today"],
    actions: ["validate_step", "add_comment"]
  },
  preparateur_peintre: {
    menus: ["planning", "today", "kanban"],
    actions: ["validate_step", "add_comment"]
  },
  laveur: {
    menus: ["planning", "today", "kanban"],
    actions: ["validate_step", "add_comment"]
  },
};

export default function UsersManagementTab() {
  const [users, setUsers] = useState([]);
  const [specialites, setSpecialites] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [permDialog, setPermDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    nom: "",
    prenom: "",
    role: "collaborateur",
    specialite: ""
  });
  
  const [editUser, setEditUser] = useState({
    id: "",
    username: "",
    password: "",
    nom: "",
    prenom: "",
    specialite: ""
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, specsRes, settingsRes] = await Promise.all([
        api.get("/users"),
        api.get("/specialites"),
        api.get("/settings")
      ]);
      setUsers(usersRes.data);
      setSpecialites(specsRes.data);
      
      // Charger les permissions depuis les settings ou utiliser les valeurs par défaut
      const savedPerms = settingsRes.data?.permissions || {};
      const mergedPerms = { ...DEFAULT_PERMISSIONS };
      Object.keys(savedPerms).forEach(key => {
        mergedPerms[key] = savedPerms[key];
      });
      setPermissions(mergedPerms);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
    setLoading(false);
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.nom || !newUser.prenom) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/register", newUser);
      toast.success("Collaborateur ajouté");
      setDialogOpen(false);
      setNewUser({ username: "", password: "", nom: "", prenom: "", role: "collaborateur", specialite: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const handleEditUser = async () => {
    if (!editUser.nom || !editUser.prenom) {
      toast.error("Veuillez remplir le nom et prénom");
      return;
    }
    setSaving(true);
    try {
      const updateData = {
        nom: editUser.nom,
        prenom: editUser.prenom,
        specialite: editUser.specialite
      };
      if (editUser.password) {
        updateData.password = editUser.password;
      }
      await api.put(`/users/${editUser.id}`, updateData);
      toast.success("Collaborateur modifié");
      setEditDialog(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
    setSaving(false);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success("Collaborateur supprimé");
      setDeleteDialog(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { permissions });
      toast.success("Permissions sauvegardées");
      setPermDialog(null);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  };

  const togglePermission = (specialite, type, item) => {
    setPermissions(prev => {
      const current = prev[specialite] || { menus: [], actions: [] };
      const list = current[type] || [];
      const newList = list.includes(item)
        ? list.filter(i => i !== item)
        : [...list, item];
      return {
        ...prev,
        [specialite]: { ...current, [type]: newList }
      };
    });
  };

  const getSpecialiteLabel = (name) => {
    const spec = specialites.find(s => s.name === name);
    return spec?.label || name;
  };

  const openEditDialog = (user) => {
    setEditUser({
      id: user.id,
      username: user.username,
      password: "",
      nom: user.nom,
      prenom: user.prenom,
      specialite: user.specialite
    });
    setEditDialog(user);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Équipe ({users.length})
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Droits d'accès
          </TabsTrigger>
        </TabsList>

        {/* Onglet Équipe */}
        <TabsContent value="users">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Gestion de l'équipe
                </CardTitle>
                <CardDescription>Gérer les comptes utilisateurs</CardDescription>
              </div>
              <Button onClick={() => setDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-white"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full ${user.role === "admin" ? "bg-red-100" : "bg-slate-100"}`}>
                        {user.role === "admin" ? (
                          <Shield className="h-5 w-5 text-red-600" />
                        ) : (
                          <User className="h-5 w-5 text-slate-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{user.prenom} {user.nom}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-2">
                          <Key className="h-3 w-3" />
                          {user.username}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={user.role === "admin" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}>
                        {user.role === "admin" ? "Admin" : getSpecialiteLabel(user.specialite)}
                      </Badge>
                      {user.role !== "admin" && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-600 hover:bg-slate-100"
                            onClick={() => openEditDialog(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => setDeleteDialog(user)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Permissions */}
        <TabsContent value="permissions">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Configuration des droits d'accès
              </CardTitle>
              <CardDescription>
                Définissez les menus et actions accessibles pour chaque spécialité
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {specialites.filter(s => s.name !== "admin" && s.name !== "patron").map((spec) => {
                  const specPerms = permissions[spec.name] || { menus: [], actions: [] };
                  return (
                    <div key={spec.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setPermDialog(permDialog === spec.name ? null : spec.name)}
                        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <User className="h-5 w-5 text-slate-600" />
                          <span className="font-semibold text-slate-900">{spec.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <span>{specPerms.menus?.length || 0} menus</span>
                          <span>•</span>
                          <span>{specPerms.actions?.length || 0} actions</span>
                          <Settings2 className="h-4 w-4 ml-2" />
                        </div>
                      </button>
                      
                      {permDialog === spec.name && (
                        <div className="p-4 border-t border-slate-200 bg-white space-y-6">
                          {/* Menus */}
                          <div>
                            <p className="font-semibold text-slate-700 mb-3">Menus accessibles</p>
                            <div className="grid grid-cols-2 gap-2">
                              {AVAILABLE_MENUS.map((menu) => (
                                <label
                                  key={menu.id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                    specPerms.menus?.includes(menu.id)
                                      ? "bg-emerald-50 border-emerald-300"
                                      : "bg-white border-slate-200 hover:border-slate-300"
                                  }`}
                                >
                                  <Switch
                                    checked={specPerms.menus?.includes(menu.id)}
                                    onCheckedChange={() => togglePermission(spec.name, "menus", menu.id)}
                                  />
                                  <div>
                                    <p className="font-medium text-sm">{menu.label}</p>
                                    <p className="text-xs text-slate-500">{menu.description}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div>
                            <p className="font-semibold text-slate-700 mb-3">Actions autorisées</p>
                            <div className="grid grid-cols-2 gap-2">
                              {AVAILABLE_ACTIONS.map((action) => (
                                <label
                                  key={action.id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                    specPerms.actions?.includes(action.id)
                                      ? "bg-emerald-50 border-emerald-300"
                                      : "bg-white border-slate-200 hover:border-slate-300"
                                  }`}
                                >
                                  <Switch
                                    checked={specPerms.actions?.includes(action.id)}
                                    onCheckedChange={() => togglePermission(spec.name, "actions", action.id)}
                                  />
                                  <div>
                                    <p className="font-medium text-sm">{action.label}</p>
                                    <p className="text-xs text-slate-500">{action.description}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div className="flex justify-end pt-2 border-t">
                            <Button onClick={handleSavePermissions} disabled={saving} className="bg-red-600 hover:bg-red-700">
                              {saving ? <div className="spinner mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                              Enregistrer
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un collaborateur</DialogTitle>
            <DialogDescription>Créer un nouveau compte utilisateur</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={newUser.prenom}
                  onChange={(e) => setNewUser({ ...newUser, prenom: e.target.value })}
                  placeholder="Jean"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newUser.nom}
                  onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })}
                  placeholder="Dupont"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Identifiant *</Label>
                <Input
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="jdupont"
                />
              </div>
              <div className="space-y-2">
                <Label>Mot de passe *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Spécialité *</Label>
              <Select
                value={newUser.specialite}
                onValueChange={(v) => setNewUser({ ...newUser, specialite: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une spécialité" />
                </SelectTrigger>
                <SelectContent>
                  {specialites.filter(s => s.name !== "admin").map((spec) => (
                    <SelectItem key={spec.id} value={spec.name}>{spec.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddUser} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le collaborateur</DialogTitle>
            <DialogDescription>Modifier les informations de {editDialog?.prenom} {editDialog?.nom}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  value={editUser.prenom}
                  onChange={(e) => setEditUser({ ...editUser, prenom: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={editUser.nom}
                  onChange={(e) => setEditUser({ ...editUser, nom: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Identifiant</Label>
              <Input value={editUser.username} disabled className="bg-slate-50" />
              <p className="text-xs text-slate-500">L'identifiant ne peut pas être modifié</p>
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={editUser.password}
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                  placeholder="Laisser vide pour ne pas changer"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Spécialité</Label>
              <Select
                value={editUser.specialite}
                onValueChange={(v) => setEditUser({ ...editUser, specialite: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {specialites.filter(s => s.name !== "admin").map((spec) => (
                    <SelectItem key={spec.id} value={spec.name}>{spec.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Annuler</Button>
            <Button onClick={handleEditUser} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <div className="spinner" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce collaborateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog?.prenom} {deleteDialog?.nom} sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteUser(deleteDialog?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
